/**
 * @puffin-core/auth-client — MFA setup operations (TOTP enroll / status /
 * backup codes / disable). `init`/`status` are always throw-style. `verify`/
 * `regenerate`/`disable` follow `mfaSetupShape`:
 *   - 'result' (pos): never-throws result-objects, object args.
 *   - 'throw'  (siblings): positional args, throw on error, return the payload.
 *
 * Endpoint paths are taken from `ctx.endpoints` so per-app divergence (e.g.
 * port's `/mfa/setup/totp/begin`) is config, not code.
 *
 * @module @puffin-core/auth-client/operations/mfaSetup
 */

import { extractData, authError, networkError } from '../internal.js';

const NETWORK_ERROR = 'Network error. Please check your connection and try again.';

/**
 * The ONE failure result the result-shape operations return, in the fleet vocabulary (`error` · `errorCode` ·
 * `errorDetails`). The server's CODE rides on it, so a screen words a
 * refusal from its catalog (a signed-in person's wrong code is 403 `CREDENTIAL_INCORRECT`, N139) instead of
 * rendering the server's English sentence.
 *
 * A failure CAUGHT here (no token, a transport throw) is answered with the operation's own sentence, never the thrown
 * message (N152 / N138, `web/no-thrown-message-as-copy`): its code rides beside it for the screen's catalog.
 *
 * @param {string} error - the message
 * @param {{errorCode?: ?string, errorDetails?: *}} [source] - the thrown or built error carrying the server's code
 * @returns {{success: false, error: string, errorCode: ?string, errorDetails: *}}
 */
function refused(error, source) {
  return {
    success: false,
    error,
    errorCode: source?.errorCode ?? null,
    errorDetails: source?.errorDetails ?? null,
  };
}

/** A non-2xx transport result as the ONE failure result. */
function refusedBy(res, fallback) {
  const err = authError(res, fallback);
  return refused(err.message, err);
}

/**
 * @param {Object} ctx
 * @returns {Object} MFA-setup methods
 */
export function buildMfaSetupOperations(ctx) {
  // No default on `mfaSetupShape`: `createAuthService` is the single source of
  // its default ('throw'), and the consumer below checks `=== 'result'` — so
  // undefined falls to the throw shape either way (no duplicated default).
  const { request, endpoints, mustHaveToken, mfaSetupShape } = ctx;

  async function initMfaSetup(params = {}) {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.initMfaSetup,
      body: { password: params.password },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to start MFA setup');
    return extractData(res.body);
  }

  async function getMfaStatus() {
    mustHaveToken();
    const res = await request({ method: 'GET', path: endpoints.getMfaStatus, auth: 'storage' });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to get MFA status');
    return extractData(res.body);
  }

  // --- result-object shape (pos) ---

  async function verifyMfaSetupResult({ code, methods = ['totp'] } = {}) {
    if (!code) return refused('Verification code is required');
    try {
      mustHaveToken();
      const res = await request({
        method: 'POST',
        path: endpoints.verifyMfaSetup,
        body: { code, methods },
        auth: 'storage',
      });
      if (res.networkError) return refused(NETWORK_ERROR);
      if (!res.ok) return refusedBy(res, 'MFA setup verification failed');
      const rd = extractData(res.body);
      return {
        success: true,
        enabled: rd?.enabled,
        methods: rd?.methods,
        backupCodes: rd?.backupCodes,
      };
    } catch (error) {
      return refused('MFA setup verification failed', error);
    }
  }

  async function regenerateBackupCodesResult({ code } = {}) {
    if (!code) return refused('Current TOTP code is required');
    try {
      mustHaveToken();
      const res = await request({
        method: 'POST',
        path: endpoints.regenerateBackupCodes,
        body: { code },
        auth: 'storage',
      });
      if (res.networkError) return refused(NETWORK_ERROR);
      if (!res.ok) return refusedBy(res, 'Failed to regenerate backup codes');
      return { success: true, backupCodes: extractData(res.body)?.backupCodes };
    } catch (error) {
      return refused('Failed to regenerate backup codes', error);
    }
  }

  async function disableMfaResult({ code, method = 'totp' } = {}) {
    if (!code) return refused('Verification code is required');
    try {
      mustHaveToken();
      const res = await request({
        method: 'POST',
        path: endpoints.disableMfa,
        body: { code, method },
        auth: 'storage',
      });
      if (res.networkError) return refused(NETWORK_ERROR);
      if (!res.ok) return refusedBy(res, 'Failed to disable MFA');
      return { success: true };
    } catch (error) {
      return refused('Failed to disable MFA', error);
    }
  }

  // --- throw shape (siblings) ---

  async function verifyMfaSetupThrow(code) {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.verifyMfaSetup,
      body: { code },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'MFA verification failed');
    return extractData(res.body);
  }

  async function regenerateBackupCodesThrow(code) {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.regenerateBackupCodes,
      body: { code },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to regenerate backup codes');
    return extractData(res.body);
  }

  async function disableMfaThrow(code, method = 'totp') {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.disableMfa,
      body: { code, method },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR);
    if (!res.ok) throw authError(res, 'Failed to disable MFA');
    return extractData(res.body);
  }

  if (mfaSetupShape === 'result') {
    return {
      initMfaSetup,
      getMfaStatus,
      verifyMfaSetup: verifyMfaSetupResult,
      regenerateMfaBackupCodes: regenerateBackupCodesResult,
      disableMfa: disableMfaResult,
    };
  }
  return {
    initMfaSetup,
    getMfaStatus,
    verifyMfaSetup: verifyMfaSetupThrow,
    regenerateBackupCodes: regenerateBackupCodesThrow,
    disableMfa: disableMfaThrow,
  };
}
